import React, {
  forwardRef, useCallback, useEffect, useImperativeHandle, useRef, useState,
} from 'react';
import { useTranslation } from 'react-i18next';
import {
  FormTemplate,
  FormTemplateSection,
  FormTemplateValidator,
  AFFormTemplateFormSectionName,
} from 'data/template/template.types';
import * as types from 'data/accessFunction/accessFunction.types';
import * as ipAssignmentTypes from 'data/ipAssignment/ipAssignment.types';
import { DistributorConfigurationTableData } from 'data/distributorConfiguration/distributorConfiguration.types';
import { VpnConfigTemplate } from 'data/vpnTemplate/vpnTemplate.types';
import { VpnConfig } from 'data/vpnConfig/vpnConfig.types';
import { Network } from 'data/network/network.types';
import { FormLayoutContent, FormLayoutFooter, FormLayout } from 'components/FormLayout';
import FormButtons from 'components/FormButtons';
import FormPanel from 'components/FormPanel';
import Form, { CustomFormFieldComponent, CustomValidationFunction } from 'shared/form';
import { validateNonRepeatedProperty } from 'utils/validators/listValidators';
import { shouldValidateInterfacesQuantity } from 'routes/AccessFunction/utils/accessFunctionTemplate.adapter';
import { getFormSectionTitle, getDiameterHintMessage } from 'routes/AccessFunction/utils/accessFunctionList.adapters';
import { AppToaster, showError } from 'shared/toaster';
import { TranslationKey } from 'i18n/i18n.types';
import { AccessFunctionInterfaceFormSectionType } from 'data/accessFunction/accessFunction.types';
import { SupportedFeaturesAccessFunctions } from 'data/supportedFeatures/supportedFeatures.types';
import { TabProp } from 'shared/multi-tab/MultiTab';
import { NodeData } from 'data/nodes/nodes.types';
import { MultiselectTypeahead } from 'shared/form/custom/multiselectTypeahead';
import { SERVER_AF_TYPES } from 'data/accessFunction/accessFunction.adapter';
import FormSection from '../FormSection';
import getExistingAccessFunctionNames from '../../utils/getExistingAccessFunctionNames';
import getExistingAccessFunctionTags from '../../utils/getExistingAccessFunctionTags';
import MultifacetedFormSection from '../MultifacetedFormSection';
import './access-function-form.scss';
import { accessFunctionPortListValidator } from '../../utils/accessFunctionPortListValidator';
import validateSameNetworkIds from '../../utils/validateSameNetworkIds';
import { getSubsectionName } from '../../utils/accessFunctionTemplate.adapter';
import validateUniqueProperty from '../../utils/validateUniqueProperty';

type Props = {
  title: string,
  isEditing: boolean,
  template: FormTemplate,
  data: Partial<types.AccessFunctionFormData>
  distributorConfigs: DistributorConfigurationTableData[],
  accessFunctionByType: { [key: string]: types.AccessFunction[] },
  enabledAccessFunctions?: SupportedFeaturesAccessFunctions,
  vpnConfigTemplates?: VpnConfigTemplate[],
  vpnConfigs?: VpnConfig[],
  customFieldComponentMap?: { [key: string]: CustomFormFieldComponent },
  networks?: Network[],
  is5gcVcpEnabled?: boolean,
  shouldValidateOnRender?: boolean,
  i18nKeyPrefix?: string
  xcipioNodes?: NodeData[]
  onClickSave?: () => void
  onClickEdit?: () => void
  onClickCancelClose: () => void
  onClickFileAction?: (param: string) => void
  onFieldUpdate?: types.AccessFunctionFieldUpdateCallback
  onDistributorUpdate?: (distributorConfiguration: DistributorConfigurationTableData) => void
  onTabAdd?: (formSectionName: AccessFunctionInterfaceFormSectionType, tabs: TabProp[]) => void,
  onTabRemove?: (id: string | number, formSectionName: AccessFunctionInterfaceFormSectionType) => void,
  onFieldCreateNew?: types.AccessFunctionFieldUpdateCallback,
  onTabChange?: (tab: TabProp, formSectionName: AccessFunctionInterfaceFormSectionType) => void,
  mode: string,
}

export type Ref = {
  isValid: (forceValidation: boolean, forceTouched: boolean) => boolean
  getValues: () => types.AccessFunctionFormData
}

export const ACCESS_FUNCTION_I18N_KEY = 'accessFunction.form';

export const AccessFunctionForm = forwardRef<Ref, Props>(({
  title,
  isEditing,
  template,
  data,
  distributorConfigs,
  accessFunctionByType,
  enabledAccessFunctions,
  vpnConfigTemplates,
  vpnConfigs,
  customFieldComponentMap,
  networks,
  is5gcVcpEnabled,
  shouldValidateOnRender = false,
  i18nKeyPrefix = ACCESS_FUNCTION_I18N_KEY,
  onClickSave,
  onClickEdit,
  onClickCancelClose,
  onFieldUpdate = () => null,
  onDistributorUpdate,
  onTabAdd,
  onTabRemove,
  onFieldCreateNew = () => null,
  onTabChange,
  onClickFileAction = () => null,
  mode,
  xcipioNodes,
}: Props, ref) => {
  const generalRef = useRef<Form>(null);
  const systemInformationRef = useRef<Form>(null);
  const provisioningInterfacesRef = useRef<Form>(null);
  const dataInterfacesRef = useRef<Form>(null);
  const contentInterfacesRef = useRef<Form>(null);
  const afOptionsRef = useRef<Form>(null);
  const monitoringCentersRef = useRef<Form>(null);
  const territoriesRef = useRef<Form>(null);
  const eimsNetworkElementRef = useRef<Form>(null);
  const diameterRef = useRef<Form>(null);
  const manualEntryRef = useRef<Form>(null);
  const ipAssignmentRef = useRef<Form>(null);
  const fileImportExportRef = useRef<Form>(null);
  const usnbdAgenciesRef = useRef<Form>(null);
  const usnbdCallDataChannelsRef = useRef<Form>(null);
  const { t } = useTranslation();

  const provisioningInterfacesSubsectionName = getSubsectionName(data, template, 'provisioningInterfaces');
  const dataInterfacesSubsectionName = getSubsectionName(data, template, 'dataInterfaces');
  const contentInterfacesSubsectionName = getSubsectionName(data, template, 'contentInterfaces');
  const afOptionsSubsectionName = getSubsectionName(data, template, 'afOptions');
  const monitoringCentersSubsectionName = getSubsectionName(data, template, 'monitoringCenters');
  const territoriesSubsectionName = getSubsectionName(data, template, 'territories');
  const eimsNetworkElementSubsectionName = getSubsectionName(data, template, 'eimsNetworkElement');
  const diameterSubsectionName = getSubsectionName(data, template, 'diameter');
  const manualEntrySubsectionName = getSubsectionName(data, template, 'manualEntry');
  const fileImportExportSubsectionName = getSubsectionName(data, template, 'fileImportExport');
  const usnbdAgenciesSubsectionName = getSubsectionName(data, template, 'usnbdAgencies');
  const usnbdCallDataChannelsSubsectionName = getSubsectionName(data, template, 'usnbdCallDataChannels');

  const isValid = (forceValidation = false, forceTouched = true) => {
    const isGeneralValid = generalRef.current?.isValid(forceValidation, forceTouched) ?? true;
    const isSystemInformationValid = systemInformationRef.current?.isValid(forceValidation, forceTouched) ?? true;
    const isProvisioningInterfacesValid =
      provisioningInterfacesRef.current?.isValid(forceValidation, forceTouched) ?? true;
    const isDataInterfacesValid =
      dataInterfacesRef.current?.isValid(forceValidation, forceTouched) ?? true;
    const isContentInterfacesValid =
      contentInterfacesRef.current?.isValid(forceValidation, forceTouched) ?? true;
    const isAfOptionsValid =
      afOptionsRef.current?.isValid(forceValidation, forceTouched) ?? true;
    const isMonitoringCentersValid =
      monitoringCentersRef.current?.isValid(forceValidation, forceTouched) ?? true;
    const isTerritoriesValid =
      territoriesRef.current?.isValid(forceValidation, forceTouched) ?? true;
    const isEimsNetworkElementValid =
      eimsNetworkElementRef.current?.isValid(forceValidation, forceTouched) ?? true;
    const isDiameterValid =
      diameterRef.current?.isValid(forceValidation, forceTouched) ?? true;
    const isManualEntryValid =
      manualEntryRef.current?.isValid(forceValidation, forceTouched) ?? true;
    const isUsnbdAgenciesValid =
      usnbdAgenciesRef.current?.isValid(forceValidation, forceTouched) ?? true;
    const isUsnbdCallDataChannelsValid =
      usnbdCallDataChannelsRef.current?.isValid(forceValidation, forceTouched) ?? true;

    const hasAtLeastOneInterface =
      dataInterfacesRef.current?.getValues().afInterfaceFormData != null
      || provisioningInterfacesRef.current?.getValues().afInterfaceFormData != null
      || contentInterfacesRef.current?.getValues().afInterfaceFormData != null
      || afOptionsRef.current?.getValues().afInterfaceFormData != null;

    const isInterfacesQuantityValid = shouldValidateInterfacesQuantity(
      template,
      provisioningInterfacesSubsectionName,
      dataInterfacesSubsectionName,
      contentInterfacesSubsectionName,
      afOptionsSubsectionName,
      eimsNetworkElementSubsectionName,
    ) ? hasAtLeastOneInterface : true;

    AppToaster.clear();

    if (!isInterfacesQuantityValid) {
      showError(renderRequiredInterfaceErrorMessage(), 5000);
    }

    return isGeneralValid &&
      isSystemInformationValid &&
      isProvisioningInterfacesValid &&
      isDataInterfacesValid &&
      isContentInterfacesValid &&
      isAfOptionsValid &&
      isInterfacesQuantityValid &&
      isMonitoringCentersValid &&
      isTerritoriesValid &&
      isEimsNetworkElementValid &&
      isDiameterValid &&
      isManualEntryValid &&
      isUsnbdAgenciesValid &&
      isUsnbdCallDataChannelsValid;
  };

  const getValues = (): types.AccessFunctionFormData => {
    const formValues: types.AccessFunctionFormData = {
      ...generalRef.current?.getValues(),
      ...systemInformationRef.current?.getValues(),
      vpnConfig: [],
    };

    const provisioningInterfaces = provisioningInterfacesRef.current?.getValues();
    if (provisioningInterfaces != null) {
      if (provisioningInterfaces.afInterfaceFormData) {
        formValues.provisioningInterfaces = provisioningInterfaces.afInterfaceFormData;
      }
      if (provisioningInterfaces.vpnConfigData) {
        formValues.vpnConfig?.push(provisioningInterfaces.vpnConfigData);
      }
    }

    const dataInterfaces = dataInterfacesRef.current?.getValues();
    if (dataInterfaces != null) {
      if (dataInterfaces.afInterfaceFormData) {
        formValues.dataInterfaces = dataInterfaces.afInterfaceFormData;
      }
      if (dataInterfaces.vpnConfigData) {
        formValues.vpnConfig?.push(dataInterfaces.vpnConfigData);
      }
    }

    const contentInterfaces = contentInterfacesRef.current?.getValues();
    if (contentInterfaces != null) {
      if (contentInterfaces.afInterfaceFormData) {
        formValues.contentInterfaces = contentInterfaces.afInterfaceFormData;
      }
      if (contentInterfaces.vpnConfigData) {
        formValues.vpnConfig?.push(contentInterfaces.vpnConfigData);
      }
    }

    const afOptions = afOptionsRef.current?.getValues();
    if (afOptions != null) {
      if (afOptions.afInterfaceFormData) {
        formValues.afOptions = afOptions.afInterfaceFormData;
      }
      if (afOptions.vpnConfigData) {
        formValues.vpnConfig?.push(afOptions.vpnConfigData);
      }
    }

    const monitoringCenters = monitoringCentersRef.current?.getValues();
    if (monitoringCenters?.afInterfaceFormData != null) {
      formValues.monitoringCenters = monitoringCenters.afInterfaceFormData;
    }

    const territories = territoriesRef.current?.getValues();
    if (territories?.afInterfaceFormData != null) {
      formValues.territories = territories.afInterfaceFormData;
    }

    const eimsNetworkElement = eimsNetworkElementRef.current?.getValues();
    if (eimsNetworkElement?.afInterfaceFormData != null) {
      formValues.eimsNetworkElement = eimsNetworkElement.afInterfaceFormData;
    }

    const diameter = diameterRef.current?.getValues();
    if (diameter?.afInterfaceFormData != null) {
      formValues.diameter = diameter.afInterfaceFormData;
    }

    const usnbdAgencies = usnbdAgenciesRef.current?.getValues();
    if (usnbdAgencies?.afInterfaceFormData != null) {
      formValues.usnbdAgencies = usnbdAgencies.afInterfaceFormData;
    }

    const usnbdCallDataChannels = usnbdCallDataChannelsRef.current?.getValues();
    if (usnbdCallDataChannels?.afInterfaceFormData != null) {
      formValues.usnbdCallDataChannels = usnbdCallDataChannels.afInterfaceFormData;
    }

    return formValues;
  };

  const getIpAssignmentValues = (): [] | ipAssignmentTypes.IpAddressFormData[] => {
    return ipAssignmentRef.current?.getValues();
  };

  const getFileLocationValue = (): string => {
    let fileLocationValue = '';
    const fileLocation = fileImportExportRef.current?.getValues();

    if (fileLocation?.afInterfaceFormData) {
      fileLocationValue = fileLocation.afInterfaceFormData[0]?.fileLocation;
    }

    return fileLocationValue;
  };

  const getManualEntryValues = (): ipAssignmentTypes.ManualEntryFormData | undefined => {
    const manualEntry = manualEntryRef.current?.getValues();
    if (manualEntry?.afInterfaceFormData) {
      const formValues = {
        servId: manualEntry.afInterfaceFormData[0].servId,
        servType: manualEntry.afInterfaceFormData[0].servType,
        forceOp: manualEntry.afInterfaceFormData[0].forceOp,
      }

      return formValues;
    }
  };

  const nonRepeatedOriginalHostValidator = useCallback<CustomValidationFunction>((_propFields?: unknown,
    _stateFields?: unknown,
    _value?: string,
    _lastSavedValue?: string,
    validator?: FormTemplateValidator,
    form?: Form | undefined) => {

    let valid = true;
    let message = '';
    const dataInterfacesValues = getValues().dataInterfaces;

    if (dataInterfacesValues) {
      const repeatedValues = dataInterfacesValues.filter((value) => value.orighost === _value);
      if (repeatedValues.length > 1) {
        valid = false;
        message = t(`${i18nKeyPrefix}.fieldError.${validator?.customValidationFunction}` as TranslationKey) ?? '';
      }
    }

    return { isValid: valid, errorMessage: message };
  }, [i18nKeyPrefix, t]);

  const nonRepeatedPropertyValidator = useCallback<CustomValidationFunction>((
    _propFields?: unknown,
    _stateFields?: unknown,
    _value?: string,
    _lastSavedValue?: string,
    validator?: FormTemplateValidator,
    form?: Form | undefined,
  ) => {

    const valid = validateNonRepeatedProperty(
      form?.getValues().name,
      getExistingAccessFunctionNames(accessFunctionByType, form?.getValues()?.id),
    );

    let message = '';
    if (!valid && validator?.customValidationFunction) {
      message = t(`${i18nKeyPrefix}.fieldError.${validator.customValidationFunction}` as TranslationKey) ?? '';
    }

    return { isValid: valid, errorMessage: message };
  }, [accessFunctionByType, i18nKeyPrefix, t]);

  const nonRepeatedTagPropertyValidator = useCallback<CustomValidationFunction>((
    _propFields?: unknown,
    _stateFields?: unknown,
    _value?: string,
    _lastSavedValue?: string,
    validator?: FormTemplateValidator,
    form?: Form | undefined,
  ) => {
    const valid = validateNonRepeatedProperty(
      form?.getValues().tag,
      getExistingAccessFunctionTags(accessFunctionByType, form?.getValues()?.id),
    );

    let message = '';
    if (!valid && validator?.customValidationFunction) {
      message = t(`${i18nKeyPrefix}.fieldError.${validator?.customValidationFunction}` as TranslationKey) ?? '';
    }

    return { isValid: valid, errorMessage: message };
  }, [accessFunctionByType]);

  const nonRepeatedIPAddressValidator = useCallback<CustomValidationFunction>((
    _propFields?: unknown,
    _stateFields?: unknown,
    _value?: string,
    _lastSavedValue?: string,
    validator?: FormTemplateValidator,
    form?: Form | undefined,
  ) => {
    const formValues = getValues();
    let valid = true;
    let message = '';

    if (formValues) {
      if (formValues.dataInterfaces && formValues.dataInterfaces.length > 1) {
        const repeatedIP = formValues.dataInterfaces.filter((ip) => ip.ipaddr === _value);
        valid = !(repeatedIP.length > 1);
      }
    }

    if (!valid && validator?.customValidationFunction) {
      message = t(`${i18nKeyPrefix}.fieldError.${validator?.customValidationFunction}` as TranslationKey) ?? '';
    }

    return { isValid: valid, errorMessage: message };
  }, [i18nKeyPrefix, t]);

  const nonRepeatedLEAValidator = useCallback<CustomValidationFunction>((
    _propFields?: unknown,
    _stateFields?: unknown,
    _value?: string,
    _lastSavedValue?: string,
    validator?: FormTemplateValidator,
    form?: Form | undefined,
  ) => {
    const formValues = getValues();
    let valid = true;
    let message = '';

    if (formValues) {
      if (formValues.dataInterfaces && formValues.dataInterfaces.length > 1) {
        const repeatedIP = formValues.dataInterfaces.filter((lea) => lea.lea === _value);
        valid = !(repeatedIP.length > 1);
      }
    }

    if (!valid && validator?.customValidationFunction) {
      message = t(`${i18nKeyPrefix}.fieldError.${validator?.customValidationFunction}` as TranslationKey) ?? '';
    }

    return { isValid: valid, errorMessage: message };
  }, [i18nKeyPrefix, t]);

  const nonRepeatedDeliveryFormatValidator = useCallback<CustomValidationFunction>((
    _propFields?: unknown,
    _stateFields?: unknown,
    _value?: string,
    _lastSavedValue?: string,
    validator?: FormTemplateValidator,
    form?: Form | undefined,
  ) => {

    let valid = true;

    const formValues = getValues();

    if (formValues) {
      if (formValues.dataInterfaces && formValues.dataInterfaces.length > 1) {
        valid = !(formValues.dataInterfaces[0].deliveryFormat === formValues.dataInterfaces[1].deliveryFormat);
      }
    }

    let message = '';
    if (!valid && validator?.customValidationFunction) {
      message = t(`${i18nKeyPrefix}.fieldError.${validator?.customValidationFunction}` as TranslationKey) ?? '';
    }

    return { isValid: valid, errorMessage: message };
  }, [i18nKeyPrefix, t]);

  const portListValidator = (
    _propFields?: unknown,
    stateFields?: unknown,
    value?: string,
    _lastSavedValue?: string,
    validator?: FormTemplateValidator,
    _form?: Form | undefined,
  ) => {
    const fields = stateFields as { [fieldName: string]: { value: string } };
    const numberOfPorts = fields?.maxx2connsperaf.value ?? '1';

    const valid = accessFunctionPortListValidator(value, +numberOfPorts);

    let message = '';
    if (!valid && validator?.customValidationFunction) {
      message = t(`${i18nKeyPrefix}.fieldError.${validator.customValidationFunction}` as TranslationKey, { count: +numberOfPorts }) ?? '';
    }

    return { isValid: valid, errorMessage: message };
  };

  const sameNetworkIdPropertyValidator = (
    _propFields?: unknown,
    _stateFields?: unknown,
    _value?: string,
    _lastSavedValue?: string,
    validator?: FormTemplateValidator,
  ) => {

    const formSectionName = validator?.formSectionName as AFFormTemplateFormSectionName;
    const interfaces = getValues()[formSectionName] || [];

    const valid = validateSameNetworkIds(interfaces);

    let message = '';
    if (!valid && validator?.customValidationFunction) {
      message = t(`${i18nKeyPrefix}.fieldError.${validator.customValidationFunction}` as TranslationKey) ?? '';
    }

    return { isValid: valid, errorMessage: message };
  };

  const uniquePropertyValidator = (
    _propFields?: unknown,
    _stateFields?: unknown,
    _value?: string,
    _lastSavedValue?: string,
    validator?: FormTemplateValidator,
  ) => {
    const formSectionName = validator?.formSectionName as AFFormTemplateFormSectionName;
    const interfaces = getValues()[formSectionName] || [];

    const valid = validateUniqueProperty('ipduid', interfaces);

    let message = '';
    if (!valid && validator?.customValidationFunction) {
      message = t(`${i18nKeyPrefix}.fieldError.${validator.customValidationFunction}` as TranslationKey) ?? '';
    }

    return { isValid: valid, errorMessage: message };
  };

  const networkIdNotFoundValidator = (
    _propFields?: unknown,
    _stateFields?: unknown,
    value?: string,
    _lastSavedValue?: string,
    validator?: FormTemplateValidator,
  ) => {
    const network = networks?.find(({ id }) => id === value);
    const valid = network != null;

    let message = '';
    if (!valid && validator?.customValidationFunction) {
      message = t(`${i18nKeyPrefix}.fieldError.${validator.customValidationFunction}` as TranslationKey) ?? '';
    }

    return { isValid: valid, errorMessage: message };
  };

  const networkIdsNotFoundValidator = (
    _propFields?: unknown,
    _stateFields?: unknown,
    value?: string,
    _lastSavedValue?: string,
    validator?: FormTemplateValidator,
  ) => {
    const networkIds = (networks ?? []).map((n) => n.id);
    const values = ((value ?? []) as MultiselectTypeahead[]).map((x) => x.value);
    const valid = values.every((v: string) => networkIds.includes(v));
    let message = '';
    if (!valid && validator?.customValidationFunction) {
      message = t(`${i18nKeyPrefix}.fieldError.${validator.customValidationFunction}` as TranslationKey) ?? '';
    }

    return { isValid: valid, errorMessage: message };
  };

  const shortIntValidator = (
    _propFields?: unknown,
    _stateFields?: unknown,
    value?: string,
    _lastSavedValue?: string,
    validator?: FormTemplateValidator,
  ) => {
    const values = value ?? '0';
    const intValue = parseInt(values, 10);
    const valid = (intValue >= 0 && intValue <= 65535);
    let message = '';
    if (!valid && validator?.customValidationFunction) {
      message = t(`${i18nKeyPrefix}.fieldError.${validator.customValidationFunction}` as TranslationKey) ?? '';
    }

    return { isValid: valid, errorMessage: message };
  };

  const customValidationFunctionMap = {
    nonRepeatedPropertyValidator,
    portListValidator,
    sameNetworkIdPropertyValidator,
    uniquePropertyValidator,
    networkIdNotFoundValidator,
    networkIdsNotFoundValidator,
    nonRepeatedTagPropertyValidator,
    nonRepeatedDeliveryFormatValidator,
    nonRepeatedOriginalHostValidator,
    nonRepeatedIPAddressValidator,
    nonRepeatedLEAValidator,
    shortIntValidator
  };

  useImperativeHandle(ref, () => ({
    isValid,
    getValues,
    getIpAssignmentValues,
    getManualEntryValues,
    getFileLocationValue,
  }));

  const renderRequiredInterfacesInstructionalText = (): JSX.Element | null => {
    if (shouldValidateInterfacesQuantity(
      template,
      provisioningInterfacesSubsectionName,
      dataInterfacesSubsectionName,
      contentInterfacesSubsectionName,
      afOptionsSubsectionName,
      eimsNetworkElementSubsectionName,
    )) {
      return (
        <p className="access-function-form-required-interfaces-instructional-text">
          {t('accessFunction.form.requiredInterface.note')}
        </p>
      );
    }

    return null;
  };

  const renderRequiredInterfaceErrorMessage = (): JSX.Element => {
    return (
      <React.Fragment>
        <p><strong>{t('accessFunction.form.requiredInterface.title')}</strong></p>
        <p>{t('accessFunction.form.requiredInterface.message')}</p>
      </React.Fragment>
    );
  };

  const renderFormPanel = (formTitle: string | null = null) => (
    <FormPanel title={formTitle}>
      <FormButtons
        isEditing={isEditing}
        onClickCancel={onClickCancelClose}
        onClickSave={onClickSave}
        onClickEdit={onClickEdit}
        onClickClose={onClickCancelClose}
      />
    </FormPanel>
  );

  const [editMFCI, setEditMFCI] = useState(true);

  const shouldEditMultiFacetedCI = () => {
    if (mode === 'edit' && template?.key === 'CISCO_ER') {
      setEditMFCI(false);
      return false;
    }
    setEditMFCI(true);
    return true;
  };

  useEffect(() => {
    shouldEditMultiFacetedCI();
  }, []);

  const shouldHideVPNConfiguration = () => {
    const SS8_AF_TYPES = ['SS8_CCPAG', 'SS8_XCF3', 'SS8_MDF2', 'SS8_MDF3', ...SERVER_AF_TYPES];
    const afType = data.type ?? '';
    return afType === 'NOK_5GAMF_LOCSVC' || SS8_AF_TYPES.includes(afType);
  }

  const shouldHideContentInterface = () => {
    if (mode === 'view' && data?.type === 'ERICSSON_LIMS' && data?.contentInterfaces?.length === 0) {
      return 'true';
    }
    return (template?.[`contentInterfaces_${contentInterfacesSubsectionName}`] as FormTemplateSection)?.metadata.hide
  }

  const afTag = (data.tag ?? data.type ?? '') as string;
  const modelOptions = enabledAccessFunctions?.[afTag]?.modelOptions;
  const modelKey: string = data.model ?? '';

  return (
    <div className="access-function-form" data-testid="AccessFunctionForm">
      <FormLayout>
        <FormLayoutContent>
          {renderFormPanel(title)}
          <FormSection
            ref={generalRef}
            name={template.formId ?? template.key}
            data={data}
            formSectionName="general"
            templateSection={template.general as FormTemplateSection}
            onFieldUpdate={onFieldUpdate}
            customValidationFunctionMap={customValidationFunctionMap}
            shouldValidateOnRender={shouldValidateOnRender}
            i18nKeyPrefix={i18nKeyPrefix}
          />
          <FormSection
            ref={systemInformationRef}
            name={template.formId ?? template.key}
            title={t('accessFunction.form.section.systemInformation')}
            data={data}
            formSectionName="systemInformation"
            templateSection={template.systemInformation as FormTemplateSection}
            onFieldUpdate={onFieldUpdate}
            customValidationFunctionMap={customValidationFunctionMap}
            shouldValidateOnRender={shouldValidateOnRender}
            i18nKeyPrefix={i18nKeyPrefix}
          />
          {renderRequiredInterfacesInstructionalText()}
          <MultifacetedFormSection
            ref={provisioningInterfacesRef}
            hideVpnConfigs={shouldHideVPNConfiguration()}
            title={getFormSectionTitle(
              data?.type || '',
              'NOK_5GAMF_LOCSVC',
              t('accessFunction.form.section.provisioningInterface'),
              t('accessFunction.form.section.locationServiceInterface'),
            )}
            template={template}
            formSectionName={AFFormTemplateFormSectionName.provisioningInterfaces}
            formSubsectionName={provisioningInterfacesSubsectionName}
            distributorConfigs={distributorConfigs}
            vpnConfigTemplates={vpnConfigTemplates}
            vpnConfigs={vpnConfigs}
            data={data}
            customFieldComponentMap={customFieldComponentMap}
            isEditing={isEditing}
            is5gcVcpEnabled={is5gcVcpEnabled}
            onDistributorUpdate={onDistributorUpdate}
            onFieldUpdate={onFieldUpdate}
            onTabAdd={onTabAdd}
            onTabRemove={onTabRemove}
            customValidationFunctionMap={customValidationFunctionMap}
            shouldValidateOnRender={shouldValidateOnRender}
            onFieldCreateNew={onFieldCreateNew}
            i18nKeyPrefix={i18nKeyPrefix}
            onTabChange={onTabChange}
            localizationDisabled={template.key === 'ERK_5GNEF'}
          />
          <MultifacetedFormSection
            ref={monitoringCentersRef}
            hideVpnConfigs
            title={t('accessFunction.form.section.monitoringCenters')}
            template={template}
            formSectionName={AFFormTemplateFormSectionName.monitoringCenters}
            formSubsectionName={monitoringCentersSubsectionName}
            distributorConfigs={distributorConfigs}
            data={data}
            customFieldComponentMap={customFieldComponentMap}
            isEditing={isEditing}
            is5gcVcpEnabled={is5gcVcpEnabled}
            onDistributorUpdate={onDistributorUpdate}
            onFieldUpdate={onFieldUpdate}
            onTabAdd={onTabAdd}
            onTabRemove={onTabRemove}
            customValidationFunctionMap={customValidationFunctionMap}
            shouldValidateOnRender={shouldValidateOnRender}
            onFieldCreateNew={onFieldCreateNew}
            i18nKeyPrefix={i18nKeyPrefix}
            hideTabTitle
            onTabChange={onTabChange}
          />
          <MultifacetedFormSection
            ref={territoriesRef}
            hideVpnConfigs
            title={t('accessFunction.form.section.territories')}
            template={template}
            formSectionName={AFFormTemplateFormSectionName.territories}
            formSubsectionName={territoriesSubsectionName}
            distributorConfigs={distributorConfigs}
            data={data}
            customFieldComponentMap={customFieldComponentMap}
            isEditing={isEditing}
            is5gcVcpEnabled={is5gcVcpEnabled}
            onDistributorUpdate={onDistributorUpdate}
            onFieldUpdate={onFieldUpdate}
            onTabAdd={onTabAdd}
            onTabRemove={onTabRemove}
            customValidationFunctionMap={customValidationFunctionMap}
            shouldValidateOnRender={shouldValidateOnRender}
            onFieldCreateNew={onFieldCreateNew}
            i18nKeyPrefix={i18nKeyPrefix}
            hideTabTitle
            onTabChange={onTabChange}
          />
          <MultifacetedFormSection
            ref={eimsNetworkElementRef}
            hideVpnConfigs
            title={t('accessFunction.form.section.eimsNetworkElement')}
            template={template}
            formSectionName={AFFormTemplateFormSectionName.eimsNetworkElement}
            formSubsectionName={eimsNetworkElementSubsectionName}
            distributorConfigs={distributorConfigs}
            data={data}
            customFieldComponentMap={customFieldComponentMap}
            isEditing={isEditing}
            is5gcVcpEnabled={is5gcVcpEnabled}
            onDistributorUpdate={onDistributorUpdate}
            onFieldUpdate={onFieldUpdate}
            onTabAdd={onTabAdd}
            onTabRemove={onTabRemove}
            customValidationFunctionMap={customValidationFunctionMap}
            shouldValidateOnRender={shouldValidateOnRender}
            onFieldCreateNew={onFieldCreateNew}
            i18nKeyPrefix={i18nKeyPrefix}
            hideTabTitle
            onTabChange={onTabChange}
          />
          <MultifacetedFormSection
            ref={usnbdAgenciesRef}
            hideVpnConfigs
            title={t('accessFunction.form.section.usnbdAgencies')}
            template={template}
            formSectionName={AFFormTemplateFormSectionName.usnbdAgencies}
            formSubsectionName={usnbdAgenciesSubsectionName}
            distributorConfigs={distributorConfigs}
            vpnConfigTemplates={vpnConfigTemplates}
            vpnConfigs={vpnConfigs}
            data={data}
            customFieldComponentMap={customFieldComponentMap}
            isEditing={isEditing}
            is5gcVcpEnabled={is5gcVcpEnabled}
            onDistributorUpdate={onDistributorUpdate}
            onFieldUpdate={onFieldUpdate}
            onTabAdd={onTabAdd}
            onTabRemove={onTabRemove}
            customValidationFunctionMap={customValidationFunctionMap}
            shouldValidateOnRender={shouldValidateOnRender}
            onFieldCreateNew={onFieldCreateNew}
            i18nKeyPrefix={i18nKeyPrefix}
            onTabChange={onTabChange}
          />
          {data.contentDeliveryVia === 'trafficManager' ||
            (data.tag === 'NOKIA_CMG' && data.model === 'SMF_C') ||
            (modelKey !== '' && modelOptions?.[modelKey]?.supportX2 === false) ?
            null : (
              <MultifacetedFormSection
                ref={dataInterfacesRef}
                hideVpnConfigs={data?.type === 'NOK_5GAMF_LOCSVC'}
                title={getFormSectionTitle(
                  data?.type || '',
                  'NOK_5GAMF_LOCSVC',
                  t('accessFunction.form.section.deliveryInterface'),
                  t('accessFunction.form.section.deliveryInterfaceLocation'),
                )}
                template={template}
                formSectionName={AFFormTemplateFormSectionName.dataInterfaces}
                formSubsectionName={dataInterfacesSubsectionName}
                distributorConfigs={distributorConfigs}
                vpnConfigTemplates={vpnConfigTemplates}
                vpnConfigs={vpnConfigs}
                data={data}
                customFieldComponentMap={customFieldComponentMap}
                isEditing={isEditing}
                is5gcVcpEnabled={is5gcVcpEnabled}
                onDistributorUpdate={onDistributorUpdate}
                onFieldUpdate={onFieldUpdate}
                onTabAdd={onTabAdd}
                onTabRemove={onTabRemove}
                customValidationFunctionMap={customValidationFunctionMap}
                shouldValidateOnRender={shouldValidateOnRender}
                onFieldCreateNew={onFieldCreateNew}
                i18nKeyPrefix={i18nKeyPrefix}
                onTabChange={onTabChange}
                localizationDisabled={template.key === 'CBS'}
              />
            )}
          <MultifacetedFormSection
            ref={usnbdCallDataChannelsRef}
            hideVpnConfigs
            title={t('accessFunction.form.section.usnbdCallDataChannels')}
            template={template}
            formSectionName={AFFormTemplateFormSectionName.usnbdCallDataChannels}
            formSubsectionName={usnbdCallDataChannelsSubsectionName}
            distributorConfigs={distributorConfigs}
            vpnConfigTemplates={vpnConfigTemplates}
            vpnConfigs={vpnConfigs}
            data={data}
            customFieldComponentMap={customFieldComponentMap}
            isEditing={isEditing}
            is5gcVcpEnabled={is5gcVcpEnabled}
            onDistributorUpdate={onDistributorUpdate}
            onFieldUpdate={onFieldUpdate}
            onTabAdd={onTabAdd}
            onTabRemove={onTabRemove}
            customValidationFunctionMap={customValidationFunctionMap}
            shouldValidateOnRender={shouldValidateOnRender}
            onFieldCreateNew={onFieldCreateNew}
            i18nKeyPrefix={i18nKeyPrefix}
            onTabChange={onTabChange}
          />
          <MultifacetedFormSection
            ref={diameterRef}
            hideVpnConfigs
            title={t('accessFunction.form.section.diameter')}
            hintMessage={getDiameterHintMessage(
              data,
              t('accessFunction.form.section.diameterHintMessage'),
              t('accessFunction.form.section.diameterHintMessage2')
            )}
            template={template}
            formSectionName={AFFormTemplateFormSectionName.diameter}
            formSubsectionName={diameterSubsectionName}
            distributorConfigs={distributorConfigs}
            data={data}
            customFieldComponentMap={customFieldComponentMap}
            isEditing={isEditing}
            is5gcVcpEnabled={is5gcVcpEnabled}
            onDistributorUpdate={onDistributorUpdate}
            onFieldUpdate={onFieldUpdate}
            onTabAdd={onTabAdd}
            onTabRemove={onTabRemove}
            customValidationFunctionMap={customValidationFunctionMap}
            shouldValidateOnRender={shouldValidateOnRender}
            onFieldCreateNew={onFieldCreateNew}
            i18nKeyPrefix={i18nKeyPrefix}
            hideTabTitle
            onTabChange={onTabChange}
          />
          {(data.tag === 'NOKIA_CMG' && data.model === 'SMF_C') ||
            (modelKey !== '' && modelOptions?.[modelKey]?.supportX3 === false) ?
            null : (
              <MultifacetedFormSection
                ref={contentInterfacesRef}
                title={t('accessFunction.form.section.contentInterface')}
                template={template}
                formSectionName={AFFormTemplateFormSectionName.contentInterfaces}
                formSubsectionName={contentInterfacesSubsectionName}
                distributorConfigs={distributorConfigs}
                vpnConfigTemplates={vpnConfigTemplates}
                vpnConfigs={vpnConfigs}
                data={data}
                customFieldComponentMap={customFieldComponentMap}
                isEditing={editMFCI}
                is5gcVcpEnabled={is5gcVcpEnabled}
                onDistributorUpdate={onDistributorUpdate}
                onFieldUpdate={onFieldUpdate}
                onTabAdd={onTabAdd}
                onTabRemove={onTabRemove}
                customValidationFunctionMap={customValidationFunctionMap}
                shouldValidateOnRender={shouldValidateOnRender}
                onFieldCreateNew={onFieldCreateNew}
                i18nKeyPrefix={i18nKeyPrefix}
                hide={shouldHideContentInterface()}
                xcipioNodes={xcipioNodes}
              />
            )}
          <MultifacetedFormSection
            ref={afOptionsRef}
            hideVpnConfigs
            title={t('accessFunction.form.section.optionsInterface')}
            template={template}
            formSectionName={AFFormTemplateFormSectionName.afOptions}
            formSubsectionName={afOptionsSubsectionName}
            distributorConfigs={distributorConfigs}
            vpnConfigTemplates={vpnConfigTemplates}
            vpnConfigs={vpnConfigs}
            data={data}
            customFieldComponentMap={customFieldComponentMap}
            isEditing={editMFCI}
            is5gcVcpEnabled={is5gcVcpEnabled}
            onDistributorUpdate={onDistributorUpdate}
            onFieldUpdate={onFieldUpdate}
            onTabAdd={onTabAdd}
            onTabRemove={onTabRemove}
            customValidationFunctionMap={customValidationFunctionMap}
            shouldValidateOnRender={shouldValidateOnRender}
            onFieldCreateNew={onFieldCreateNew}
            i18nKeyPrefix={i18nKeyPrefix}
            hide={(template?.[`afOptions_${afOptionsSubsectionName}`] as FormTemplateSection)?.metadata.hide}
            xcipioNodes={xcipioNodes}
          />
          {mode === 'create' || mode === 'edit' || mode === 'copy' ?
            (
              <MultifacetedFormSection
                ref={manualEntryRef}
                hideVpnConfigs
                title={t('accessFunction.form.section.manualEntry')}
                template={template}
                formSectionName={AFFormTemplateFormSectionName.manualEntry}
                formSubsectionName={manualEntrySubsectionName}
                distributorConfigs={distributorConfigs}
                data={data}
                customFieldComponentMap={customFieldComponentMap}
                isEditing={isEditing}
                is5gcVcpEnabled={is5gcVcpEnabled}
                onDistributorUpdate={onDistributorUpdate}
                onFieldUpdate={onFieldUpdate}
                onTabAdd={onTabAdd}
                onTabRemove={onTabRemove}
                customValidationFunctionMap={customValidationFunctionMap}
                shouldValidateOnRender={shouldValidateOnRender}
                onFieldCreateNew={onFieldCreateNew}
                i18nKeyPrefix={i18nKeyPrefix}
                hideTabTitle
                onTabChange={onTabChange}
                hideAddButton
              />
            ) : null}
          {mode === 'create' || mode === 'edit' || mode === 'copy' ?
            (
              <FormSection
                ref={ipAssignmentRef}
                name={template.formId ?? template.key}
                data={data}
                formSectionName="ipAssignment"
                templateSection={template.ipAssignment as FormTemplateSection}
                onFieldUpdate={onFieldUpdate}
                customValidationFunctionMap={customValidationFunctionMap}
                shouldValidateOnRender={shouldValidateOnRender}
                i18nKeyPrefix={i18nKeyPrefix}
                template={template}
              />
            ) : null}
          {mode === 'create' || mode === 'edit' || mode === 'copy' ?
            (
              <MultifacetedFormSection
                ref={fileImportExportRef}
                hideVpnConfigs
                title={t('accessFunction.form.section.fileImportExport')}
                template={template}
                formSectionName={AFFormTemplateFormSectionName.fileImportExport}
                formSubsectionName={fileImportExportSubsectionName}
                distributorConfigs={distributorConfigs}
                data={data}
                customFieldComponentMap={customFieldComponentMap}
                isEditing={isEditing}
                is5gcVcpEnabled={is5gcVcpEnabled}
                onDistributorUpdate={onDistributorUpdate}
                onFieldUpdate={onFieldUpdate}
                onTabAdd={onTabAdd}
                onTabRemove={onTabRemove}
                customValidationFunctionMap={customValidationFunctionMap}
                shouldValidateOnRender={shouldValidateOnRender}
                onFieldCreateNew={onFieldCreateNew}
                i18nKeyPrefix={i18nKeyPrefix}
                hideTabTitle
                onTabChange={onTabChange}
                hideAddButton
                onClickFileAction={onClickFileAction}
                mode={mode}
              />
            ) : null}
        </FormLayoutContent>
        <FormLayoutFooter>
          {renderFormPanel()}
        </FormLayoutFooter>
      </FormLayout>
    </div>
  );
});

export default AccessFunctionForm;
